"use client";

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Clock,
  ExternalLink,
  History,
  Inbox,
  KeyRound,
  Mail,
  RefreshCw,
  RotateCw,
  Send,
} from "lucide-react";
import {
  getWebhookEndpoint,
  listWebhookDeliveries,
  listWebhookEndpoints,
  updateWebhookEndpoint,
  type DeliveryListItem,
  type WebhookEndpoint,
} from "../../../services/merchantApi";
import {
  CopyButton,
  EmptyState,
  ErrorCard,
  InlineNotice,
  PageHeader,
  Section,
  SkeletonRows,
  StatTile,
  StatusPill,
  Switch,
  focusRing,
  primaryBtn,
  secondaryBtn,
} from "../ui";
import { formatAbsolute, formatUtc, hostOf } from "./format";
import { healthUi } from "./vocab";
import { WebhooksGate } from "./WebhooksGate";
import { WebhookAlertBanner } from "./WebhookAlertBanner";
import { notifyWebhooksChanged, useWebhookSummary } from "./useWebhookSummary";
import EndpointCard, { EndpointCardSkeleton, endpointDeliveriesPath, formatShortWhen } from "./EndpointCard";
import { apiErrorOf, useCountdown } from "./EndpointFormModal";
import RotateSecretModal from "./RotateSecretModal";
import SecretRevealCard, { type SecretReveal } from "./SecretRevealCard";
import DeliveryRow from "./DeliveryRow";

// ────────────────────────────────────────────────────────────────────────────
// /developers/webhooks/endpoints/:endpointId — one endpoint in full: the
// card with every action, its signing secret, its alert settings and its
// latest live deliveries. Target of the "disabled" / "recovered" emails.
//
// Refreshes itself silently every 30 s while the tab is visible (no
// skeleton, stats kept). A 404 — bad id, deleted, or another account's —
// renders the "Endpoint not found" state, never an error.
// ────────────────────────────────────────────────────────────────────────────

const ENDPOINT_ID_RE = /^whe_[A-Za-z0-9_-]{16}$/;
const POLL_MS = 30_000;
const RECENT_LIMIT = 10;

const EMAIL_KIND_LABEL: Record<string, string> = {
  failing: "retrying",
  exhausted: "failed permanently",
  disabled: "endpoint disabled",
  recovered: "recovered",
  security_notice: "settings changed",
};

/** Route component for `/developers/webhooks/endpoints/:endpointId`. Takes no props. */
export default function EndpointDetailPage() {
  const { endpointId = "" } = useParams<{ endpointId: string }>();
  return (
    <WebhooksGate>
      {({ wallet, email }) => (
        <EndpointDetail key={endpointId} wallet={wallet} email={email} endpointId={endpointId} />
      )}
    </WebhooksGate>
  );
}

type Phase =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "notfound" }
  | { kind: "error"; message: string; requestId: string | null };

type Recent =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: DeliveryListItem[]; hasMore: boolean };

function EndpointDetail({ wallet, email, endpointId }: { wallet: string; email: string | null; endpointId: string }) {
  const navigate = useNavigate();
  const { summary } = useWebhookSummary(wallet, email);
  const validId = ENDPOINT_ID_RE.test(endpointId);

  const [phase, setPhase] = useState<Phase>(validId ? { kind: "loading" } : { kind: "notfound" });
  const [endpoint, setEndpoint] = useState<WebhookEndpoint | null>(null);
  const [meta, setMeta] = useState<{ alertRecipient: string | null; secretWritesBlocked: boolean }>({
    alertRecipient: null,
    secretWritesBlocked: false,
  });
  const [recent, setRecent] = useState<Recent>({ kind: "loading" });
  const [recentRefreshing, setRecentRefreshing] = useState(false);
  const [reveal, setReveal] = useState<SecretReveal | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [alertsBusy, setAlertsBusy] = useState(false);
  const alertsBusyRef = useRef(false);
  const alertsCooldown = useCountdown();

  const version = useRef(0); // bumped by every local change to `endpoint`
  const alive = useRef(true);
  const nonce = useRef(0);
  const lastFetchAt = useRef(0);
  const endpointInFlight = useRef(false);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadEndpoint = useCallback(
    async (mode: "initial" | "silent") => {
      if (!validId) return;
      if (mode === "silent" && endpointInFlight.current) return;
      endpointInFlight.current = true;
      const ver = version.current;
      try {
        const [one, list] = await Promise.all([
          getWebhookEndpoint(wallet, email, endpointId, { includeStats: mode === "initial" }),
          // Recipient + platform flag only come with the list; the page works without them.
          mode === "initial" ? listWebhookEndpoints(wallet, email).catch(() => null) : Promise.resolve(null),
        ]);
        if (!alive.current) return;
        lastFetchAt.current = Date.now();
        if (list) {
          setMeta({
            alertRecipient: list.alertRecipient ?? null,
            secretWritesBlocked: !!list.platform?.secretWritesBlocked,
          });
        }
        setEndpoint((cur) => {
          if (cur && version.current !== ver) {
            // A local change landed while this was on the wire: keep it.
            return { ...cur, stats24h: one.endpoint.stats24h ?? cur.stats24h };
          }
          return { ...one.endpoint, stats24h: one.endpoint.stats24h ?? cur?.stats24h };
        });
        setPhase({ kind: "ready" });
      } catch (err) {
        if (!alive.current) return;
        const e = apiErrorOf(err);
        if (e.status === 404) {
          setEndpoint(null);
          setPhase({ kind: "notfound" });
        } else if (mode === "initial") {
          setPhase({ kind: "error", message: e.message || "Failed to load.", requestId: e.requestId ?? null });
        }
        // A failed background refresh keeps what is on screen.
      } finally {
        endpointInFlight.current = false;
      }
    },
    [wallet, email, endpointId, validId],
  );

  const loadRecent = useCallback(
    async (mode: "initial" | "manual" | "silent") => {
      if (!validId) return;
      if (mode === "manual") setRecentRefreshing(true);
      try {
        const res = await listWebhookDeliveries(wallet, email, {
          mode: "live",
          endpointId,
          limit: RECENT_LIMIT,
        });
        if (!alive.current) return;
        setRecent({ kind: "ready", items: res.deliveries || [], hasMore: !!res.hasMore });
      } catch (err) {
        if (!alive.current) return;
        const e = apiErrorOf(err);
        // Once rows are on screen a failed refresh keeps them.
        setRecent((cur) => (cur.kind === "ready" ? cur : { kind: "error", message: e.message || "Failed to load." }));
        if (mode === "manual") toast.error(e.message || "Couldn't refresh deliveries.");
      } finally {
        if (alive.current && mode === "manual") setRecentRefreshing(false);
      }
    },
    [wallet, email, endpointId, validId],
  );

  useEffect(() => {
    void loadEndpoint("initial");
    void loadRecent("initial");
  }, [loadEndpoint, loadRecent]);

  // Silent refresh every 30 s while visible, and on return to the tab.
  const ready = phase.kind === "ready";
  useEffect(() => {
    if (!ready) return;
    const tick = () => {
      void loadEndpoint("silent");
      void loadRecent("silent");
    };
    const id = setInterval(() => {
      if (!document.hidden) tick();
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastFetchAt.current >= POLL_MS) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [ready, loadEndpoint, loadRecent]);

  // Something changed elsewhere (banner "Mark as reviewed", another tab, a
  // delivery failing): refresh the endpoint now rather than in ≤ 30 s.
  const summaryKey =
    summary && summary.merchant
      ? [summary.enabledCount, summary.failingCount, summary.disabledCount, summary.failedDeliveriesUnacked].join("|")
      : "";
  const seenSummaryKey = useRef<string | null>(null);
  useEffect(() => {
    if (!summaryKey) return;
    const first = seenSummaryKey.current === null;
    if (seenSummaryKey.current === summaryKey) return;
    seenSummaryKey.current = summaryKey;
    if (!first && ready) void loadEndpoint("silent");
  }, [summaryKey, ready, loadEndpoint]);

  const applyLocal = useCallback((next: WebhookEndpoint) => {
    version.current += 1;
    setEndpoint((cur) => ({ ...next, stats24h: next.stats24h ?? cur?.stats24h }));
  }, []);

  const showSecret = useCallback((r: Omit<SecretReveal, "nonce">) => {
    nonce.current += 1;
    setReveal({ ...r, nonce: nonce.current });
  }, []);

  const toggleAlerts = async (next: boolean) => {
    if (!endpoint || alertsBusyRef.current || alertsCooldown.active) return;
    alertsBusyRef.current = true;
    setAlertsBusy(true);
    try {
      const res = await updateWebhookEndpoint(wallet, email, endpoint.id, { emailAlerts: next });
      applyLocal(res.endpoint);
      toast.success(next ? "Alert emails turned on." : "Alert emails turned off.");
    } catch (err) {
      const e = apiErrorOf(err);
      if (e.status === 429) alertsCooldown.start(e.retryAfter);
      toast.error(e.message || "Couldn't update alert emails.");
    } finally {
      alertsBusyRef.current = false;
      setAlertsBusy(false);
      notifyWebhooksChanged();
    }
  };

  const platform = summary && summary.merchant ? summary.platform : null;
  const secretWritesBlocked = meta.secretWritesBlocked || !!platform?.secretWritesBlocked;
  const workerDelayed = !!platform?.workerDelayed;

  // ── Non-ready states ──────────────────────────────────────────────────
  if (phase.kind !== "ready" || !endpoint) {
    return (
      <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
        <BackLink />
        {phase.kind === "notfound" ? (
          <NotFound endpointId={validId ? endpointId : null} />
        ) : phase.kind === "error" ? (
          <ErrorCard
            title="Couldn't load this endpoint"
            message={phase.message}
            requestId={phase.requestId}
            onRetry={() => {
              setPhase({ kind: "loading" });
              void loadEndpoint("initial");
              void loadRecent("initial");
            }}
          />
        ) : (
          <DetailSkeleton />
        )}
      </div>
    );
  }

  // ── Ready ─────────────────────────────────────────────────────────────
  const ep = endpoint;
  const host = ep.urlHost || hostOf(ep.url);
  const prevExpiry = ep.previousSecretExpiresAt ? new Date(ep.previousSecretExpiresAt).getTime() : NaN;
  const overlap = Number.isFinite(prevExpiry) && prevExpiry > Date.now();
  const lastMail = ep.lastAlertEmail;

  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
      <BackLink />

      <PageHeader
        icon={<Send className="w-5 h-5 text-[#61CE70]" />}
        eyebrow="Webhook endpoint"
        title={
          <>
            <span className="[overflow-wrap:anywhere]">{withDotBreaks(host)}</span>{" "}
            <StatusPill ui={healthUi(ep)} size="lg" className="align-middle relative -top-0.5" />
          </>
        }
        blurb={
          <span className="inline-flex flex-wrap items-center gap-x-1.5">
            <span className="text-gray-500">Endpoint ID</span>
            <span className="font-mono text-gray-300 break-all">{ep.id}</span>
            <CopyButton text={ep.id} label="Copy endpoint ID" className="-my-2" />
          </span>
        }
        action={<DocsLink />}
      />

      <WebhookAlertBanner wallet={wallet} email={email} variant="page" />

      {secretWritesBlocked && (
        <InlineNotice tone="warn" role="status" className="mb-6" title="Webhooks are temporarily limited on our side.">
          Existing endpoints keep receiving events; creating endpoints and rotating secrets is paused. Nothing
          is lost.
        </InlineNotice>
      )}

      {workerDelayed && (
        <InlineNotice tone="info" icon={Clock} role="status" className="mb-6" title="Deliveries are running behind.">
          Our delivery queue is slower than usual right now. Every event is queued and will be delivered —
          nothing is lost.
        </InlineNotice>
      )}

      {reveal && <SecretRevealCard key={reveal.nonce} reveal={reveal} onDismiss={() => setReveal(null)} />}

      <div className="space-y-6">
        <EndpointCard
          wallet={wallet}
          email={email}
          endpoint={ep}
          alertRecipient={meta.alertRecipient}
          secretWritesBlocked={secretWritesBlocked}
          expanded
          onChanged={applyLocal}
          onDeleted={() => navigate("/developers/webhooks", { replace: true })}
          onSecretRevealed={(s) => showSecret({ ...s, kind: "rotated" })}
        />

        {/* ── Signing secret ─────────────────────────────────────────── */}
        <Section
          icon={<KeyRound className="w-4 h-4 text-[#61CE70]" />}
          title="Signing secret"
          desc="Every delivery carries an X-CashTap-Signature header made with this secret."
          action={
            <button
              type="button"
              onClick={() => setRotateOpen(true)}
              disabled={secretWritesBlocked}
              title={secretWritesBlocked ? "Rotating secrets is paused on our side right now" : undefined}
              className={`${secondaryBtn} shrink-0 py-2 disabled:cursor-not-allowed ${focusRing}`}
            >
              <RotateCw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Rotate secret</span>
              <span className="sm:hidden">Rotate</span>
            </button>
          }
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <StatTile label="Current secret" value={`••••${ep.secretLast4}`} />
            <StatTile label="Version" value={`v${ep.secretVersion}`} />
            <div className="col-span-2 sm:col-span-1 min-w-0">
              <StatTile label="Created" value={formatAbsolute(ep.secretCreatedAt)} />
            </div>
          </div>

          {overlap ? (
            <InlineNotice icon={KeyRound} className="mt-3">
              Previous secret <span className="font-mono text-gray-300">••••{ep.previousSecretLast4 || "????"}</span>{" "}
              stays valid until{" "}
              <time dateTime={ep.previousSecretExpiresAt as string} className="text-gray-200">
                {formatAbsolute(ep.previousSecretExpiresAt)}
              </time>{" "}
              <span className="text-gray-500">({formatUtc(ep.previousSecretExpiresAt)})</span>. Until then every
              delivery carries both signatures.
            </InlineNotice>
          ) : (
            <p className="mt-3 text-xs text-gray-500">Only the current secret signs deliveries.</p>
          )}

          <p className="mt-3 text-xs text-gray-500 leading-relaxed">
            Secrets are shown once, when created or rotated, and stored encrypted. Lost yours? Rotate it — for
            24 hours both secrets verify, so you can deploy the new one without downtime.{" "}
            <a
              href="/developers/docs#webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-[#61CE70] hover:text-white rounded-sm ${focusRing}`}
            >
              How to verify signatures <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </a>
          </p>
        </Section>

        {/* ── Alerts ─────────────────────────────────────────────────── */}
        <Section
          icon={<Mail className="w-4 h-4 text-[#61CE70]" />}
          title="Alerts"
          desc="How CashTap tells you this endpoint needs attention."
        >
          <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-gray-900/30 border border-gray-800/60">
            <div className="min-w-0">
              <p className="text-white text-sm font-medium">Email me when deliveries fail</p>
              <p className="text-gray-500 text-xs mt-0.5 leading-relaxed break-words">
                {meta.alertRecipient ? (
                  <>
                    Sent to <span className="text-gray-300">{meta.alertRecipient}</span>. We always email you if
                    CashTap disables the endpoint.
                  </>
                ) : (
                  "No alert email on file — contact support to add one. Alerts still appear in your dashboard."
                )}
              </p>
            </div>
            <Switch
              checked={ep.emailAlerts}
              onChange={toggleAlerts}
              label="Email me when deliveries fail"
              busy={alertsBusy}
              disabled={alertsCooldown.active}
            />
          </div>

          {alertsCooldown.active && (
            <p role="status" className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-300 tabular-nums">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              Too many changes in a short time. {alertsCooldown.text}.
            </p>
          )}

          <div className="mt-3 text-xs">
            {!lastMail ? (
              <p className="text-gray-500">No alert emails have been sent for this endpoint yet.</p>
            ) : lastMail.ok ? (
              <p className="text-gray-400">
                Last alert email: {EMAIL_KIND_LABEL[lastMail.kind] || lastMail.kind} ·{" "}
                <time dateTime={lastMail.at} title={formatUtc(lastMail.at)}>
                  {formatShortWhen(lastMail.at)}
                </time>{" "}
                · <span className="text-[#61CE70]">sent</span>
              </p>
            ) : lastMail.reason === "daily_cap" ? (
              <p className="text-amber-300/90">
                Last alert email ({EMAIL_KIND_LABEL[lastMail.kind] || lastMail.kind},{" "}
                <time dateTime={lastMail.at}>{formatShortWhen(lastMail.at)}</time>) was not sent: today's limit of
                alert emails was reached. Alerts on this page are unaffected.
              </p>
            ) : (
              <p className="text-red-300" role="alert">
                We couldn't email you ({lastMail.reason || "delivery failed"}). Check the address on your account.
              </p>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            The dashboard banner and the sidebar badge show every alert, whether or not emails are on.
          </p>
        </Section>

        {/* ── Recent deliveries ──────────────────────────────────────── */}
        <Section
          icon={<History className="w-4 h-4 text-[#61CE70]" />}
          title="Recent deliveries"
          desc="The latest live deliveries to this endpoint. Test sends are under Deliveries → Test events."
          action={
            <Link to={endpointDeliveriesPath(ep.id)} className={`${secondaryBtn} shrink-0 py-2 ${focusRing}`}>
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          }
        >
          {recent.kind === "loading" ? (
            <div className="rounded-xl border border-gray-800/60 bg-black/20 overflow-hidden">
              <SkeletonRows rows={3} />
            </div>
          ) : recent.kind === "error" ? (
            <InlineNotice
              tone="error"
              title="Couldn't load recent deliveries"
              action={
                <button
                  type="button"
                  onClick={() => void loadRecent("manual")}
                  disabled={recentRefreshing}
                  className={`inline-flex items-center gap-1.5 py-1 text-xs text-red-200 hover:text-white disabled:opacity-60 rounded-sm ${focusRing}`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${recentRefreshing ? "animate-spin" : ""}`} /> Retry
                </button>
              }
            >
              {recent.message}
            </InlineNotice>
          ) : recent.items.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No deliveries to this endpoint yet."
              hint="They appear here as soon as a checkout session completes, expires or fails."
            />
          ) : (
            <>
              <ul className="rounded-xl border border-gray-800/60 bg-black/20 overflow-hidden divide-y divide-gray-800/50">
                {recent.items.map((d) => (
                  <DeliveryRow key={d.id} delivery={d} showEndpoint={false} />
                ))}
              </ul>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <span className="text-[11px] text-gray-500">
                  {recent.hasMore ? `Showing the latest ${RECENT_LIMIT}.` : "History is kept for 30 days."}
                </span>
                <button
                  type="button"
                  onClick={() => void loadRecent("manual")}
                  disabled={recentRefreshing}
                  className={`inline-flex items-center gap-1.5 py-1.5 text-xs text-gray-400 hover:text-white disabled:opacity-60 rounded-sm ${focusRing}`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${recentRefreshing ? "animate-spin" : ""}`} aria-hidden="true" />
                  {recentRefreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </>
          )}
        </Section>
      </div>

      <RotateSecretModal
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        wallet={wallet}
        email={email}
        endpoint={ep}
        secretWritesBlocked={secretWritesBlocked}
        onRotated={(r) => {
          applyLocal(r.endpoint);
          showSecret({
            endpointId: ep.id,
            host,
            secret: r.secret,
            kind: "rotated",
            previousSecretExpiresAt: r.previousSecretExpiresAt,
          });
          toast.success(
            r.replacedPreviousSecret ? "Secret rotated. The older overlapping secret was dropped." : "Signing secret rotated.",
          );
        }}
      />
    </div>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────────────

// "orders.example-shop.io" → wraps after a dot on narrow screens instead of
// mid-label. Text children + <wbr> only.
function withDotBreaks(host: string) {
  const parts = host.split(".");
  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 && (
        <>
          .<wbr />
        </>
      )}
    </Fragment>
  ));
}

function BackLink() {
  return (
    <Link
      to="/developers/webhooks"
      className={`inline-flex items-center gap-1.5 mb-6 py-1 text-xs text-gray-400 hover:text-white rounded-sm ${focusRing}`}
    >
      <ArrowLeft className="w-3.5 h-3.5" /> Back to webhooks
    </Link>
  );
}

function DocsLink() {
  return (
    <a
      href="/developers/docs#webhooks"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Webhook documentation (opens in a new tab)"
      className={`inline-flex items-center gap-1.5 p-2.5 sm:px-3 sm:py-2 rounded-lg border border-gray-800 bg-gray-900/40 hover:border-gray-700 text-xs font-medium text-gray-300 hover:text-white transition-colors ${focusRing}`}
    >
      <BookOpen className="w-4 h-4 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
      <span className="hidden sm:inline">Docs</span>
      <ExternalLink className="hidden sm:block w-3 h-3 text-gray-500" aria-hidden="true" />
    </a>
  );
}

function NotFound({ endpointId }: { endpointId: string | null }) {
  const historyHref = endpointId ? endpointDeliveriesPath(endpointId) : "/developers/webhooks?tab=deliveries";
  return (
    <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 p-8 sm:p-10 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-gray-800/60 flex items-center justify-center">
        <Inbox className="w-6 h-6 text-gray-500" aria-hidden="true" />
      </div>
      <h1 className="mt-4 text-white text-lg font-semibold">Endpoint not found</h1>
      <p className="mt-1 text-gray-400 text-sm max-w-sm mx-auto leading-relaxed">
        It may have been deleted. Its past deliveries are still in the delivery history.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Link to="/developers/webhooks" className={`${primaryBtn} ${focusRing}`}>
          <ArrowLeft className="w-4 h-4" /> Back to webhooks
        </Link>
        <Link to={historyHref} className={`${secondaryBtn} py-2 ${focusRing}`}>
          <History className="w-3.5 h-3.5" /> Delivery history
        </Link>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="mb-8 flex items-center gap-3 animate-pulse">
        <div className="w-11 h-11 rounded-2xl bg-gray-800/60 shrink-0" />
        <div className="space-y-2 min-w-0 flex-1">
          <div className="h-2.5 w-32 rounded bg-gray-800/60" />
          <div className="h-6 w-64 max-w-full rounded bg-gray-800/70" />
        </div>
      </div>
      <div className="space-y-6">
        <EndpointCardSkeleton />
        <div className="rounded-2xl border border-gray-800/80 bg-gradient-to-br from-[#0F1419]/70 to-[#0B0F16]/70 p-5 sm:p-6 animate-pulse">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-800/60 shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-3.5 w-32 rounded bg-gray-800/70" />
              <div className="h-2.5 w-64 max-w-full rounded bg-gray-800/40" />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-[62px] rounded-xl border border-gray-800 bg-gray-900/40" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
